import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, ShieldCheck, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function AdminUsers() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    setProfiles((data ?? []) as Profile[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleRole = async (p: Profile) => {
    if (p.id === user?.id) { toast.error('Nie możesz zmienić własnej roli.'); return; }
    if (!confirm(`Zmienić rolę użytkownika "${p.username}" z "${p.role}" na "${p.role === 'admin' ? 'user' : 'admin'}"?`)) return;
    setToggling(p.id);
    const newRole = p.role === 'admin' ? 'user' : 'admin';
    const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', p.id);
    if (error) toast.error(error.message);
    else { toast.success(`Rola zmieniona na ${newRole}.`); load(); }
    setToggling(null);
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-black text-foreground" style={{ fontFamily: 'Orbitron, sans-serif' }}>UŻYTKOWNICY</h1>
        <p className="text-muted-foreground mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Zarządzaj rolami i kontami użytkowników</p>
      </div>

      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'oklch(0.25 0.045 280)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'oklch(0.14 0.028 280)', borderBottom: '1px solid oklch(0.25 0.045 280)' }}>
              {['Użytkownik', 'ID', 'Rola', 'Dołączył', 'Akcja'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-bold tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{h.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(4)].map((_, i) => (
                <tr key={i} style={{ borderBottom: '1px solid oklch(0.22 0.04 280)' }}>
                  {[...Array(5)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 rounded animate-pulse" style={{ background: 'var(--muted)', width: '70%' }} /></td>)}
                </tr>
              ))
            ) : profiles.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">Brak użytkowników.</td></tr>
            ) : profiles.map((p) => {
              const isMe = p.id === user?.id;
              return (
                <tr key={p.id}
                  style={{ borderBottom: '1px solid oklch(0.20 0.035 280)', background: 'oklch(0.16 0.03 280)' }}
                  onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = 'oklch(0.18 0.04 280)'}
                  onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'oklch(0.16 0.03 280)'}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
                        {p.username[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                          {p.username} {isMe && <span className="text-xs text-primary">(Ty)</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-muted-foreground font-mono">{p.id.slice(0, 8)}…</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                      style={{
                        background: p.role === 'admin' ? 'oklch(0.65 0.32 280 / 0.18)' : 'oklch(0.22 0.04 280)',
                        color: p.role === 'admin' ? 'var(--primary)' : 'var(--muted-foreground)',
                        border: p.role === 'admin' ? '1px solid oklch(0.65 0.32 280 / 0.4)' : '1px solid transparent',
                        fontFamily: 'JetBrains Mono, monospace',
                      }}>
                      {p.role === 'admin' ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                      {p.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground font-mono">
                    {new Date(p.created_at).toLocaleDateString('pl-PL')}
                  </td>
                  <td className="px-4 py-3">
                    <motion.button
                      whileHover={!isMe ? { scale: 1.04 } : {}}
                      disabled={isMe || toggling === p.id}
                      onClick={() => toggleRole(p)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        background: p.role === 'admin' ? 'oklch(0.62 0.28 15 / 0.15)' : 'oklch(0.65 0.32 280 / 0.15)',
                        color: p.role === 'admin' ? 'var(--destructive)' : 'var(--primary)',
                      }}
                    >
                      {toggling === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (p.role === 'admin' ? <Shield className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />)}
                      {p.role === 'admin' ? 'Odbierz admina' : 'Nadaj admina'}
                    </motion.button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
